import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
    collection,
    doc,
    getDocs,
} from "firebase/firestore";
import {
    createBooking,
    computeBookingProviderAmount,
    bookingServices,
    normalizeCommissionOverrides,
} from "../lib/bookingSync";
import CommissionOverrideFields from "../components/CommissionOverrideFields";

/**
 * CreateBookingForm
 * -----------------
 * Admin-side booking creation. Mirrors the Flutter app's `buildBooking()` /
 * `BookingModel.toFirestore()` contract exactly: a booking is a *denormalized*
 * document, so choosing a user, a primary service (category) and a provider
 * copies their name / phone / image / color onto the booking itself.
 *
 * Written to the `bookings` collection with the same field names the mobile app
 * reads (`userPhoneNumber`, `providerPhoneNumber`, `imageOfCategory`, ...).
 *
 * The customer is looked up by PHONE NUMBER. Matching existing accounts appear
 * in a dropdown; the admin picks one to attach its `userId` to the booking. If
 * no account is picked, the booking is written without a `userId` and the
 * `ensureBookingUser` Cloud Function creates/links the user account from
 * `userPhoneNumber` on document create.
 */

// No "Pending": a provider is required here, and assigning one accepts the
// booking (see bookingSync).
const STATUS_OPTIONS = [
    { value: "confirmed", label: "Accepted" },
    { value: "completed", label: "Completed" },
    { value: "canceled", label: "Rejected" },
];

const EMPTY_FORM = {
    userId: "", // set only when the phone matches an existing user
    userName: "",
    phoneNumber: "",
    categoryId: "",
    serviceProviderId: "",
    address: "",
    pincode: "",
    problemDescription: "",
    payableAmount: "",
    usedCashpoints: "",
    status: "confirmed",
    // secondary service (optional – service clubbing)
    secondaryCategoryId: "",
    secondaryServiceOptionTitle: "",
    // The secondary service's share of payableAmount (primary gets the rest).
    secondaryAmount: "",
    // Optional per-service commission overrides for this booking:
    // { [categoryId]: { commissionType, commissionValue } }.
    commissionOverrides: {},
};

// Active and works in the primary category and, when set, the secondary one.
const providerCovers = (provider, categoryId, secondaryCategoryId) =>
    !!provider &&
    provider.active !== false &&
    !!provider.categoryIds?.includes(categoryId) &&
    (!secondaryCategoryId || !!provider.categoryIds?.includes(secondaryCategoryId));

const CreateBookingForm = ({ onBeforeCreate, onCreated }) => {
    const [users, setUsers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [providers, setProviders] = useState([]);

    const [form, setForm] = useState(EMPTY_FORM);
    const [validationErrors, setValidationErrors] = useState({});
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [usersSnap, categoriesSnap, providersSnap] = await Promise.all([
                    getDocs(collection(db, "users")),
                    getDocs(collection(db, "Categories")),
                    getDocs(collection(db, "ServiceProviders")),
                ]);

                // Loaded once; the phone lookup runs client-side against this list.
                setUsers(
                    usersSnap.docs
                        .map((d) => ({ id: d.id, ...d.data() }))
                        .filter((u) => u.anonymousUser !== true)
                );
                setCategories(
                    categoriesSnap.docs
                        .map((d) => ({ id: d.id, ...d.data() }))
                        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                );
                setProviders(
                    providersSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
                );
            } catch (err) {
                console.error("Error loading booking form data:", err);
                setError("Failed to load users / services / providers.");
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, []);

    // ---- derived helpers -------------------------------------------------

    const getUserName = (u) => u?.name || u?.fullName || "Unnamed user";
    const getUserPhone = (u) => u?.phoneNumber || u?.phone_number || "";

    // Last 10 digits — used ONLY for matching/comparison, mirroring the Cloud
    // Function's normalizePhoneNumber(). Never stored on the booking.
    const normalizePhone = (value) =>
        (value || "").toString().replace(/\D/g, "").slice(-10);

    // E.164 form (+91XXXXXXXXXX) — what Firebase Auth stores and what the app
    // writes to userPhoneNumber. Everything persisted to Firestore uses this.
    const toE164 = (value) => {
        const digits = normalizePhone(value);
        return digits.length === 10 ? `+91${digits}` : "";
    };

    const phoneDigits = normalizePhone(form.phoneNumber);

    // Existing users whose phone matches what's been typed so far. Once the
    // admin has entered enough digits we surface these in a dropdown so they can
    // pick the exact account — that's how we lock in a real userId.
    const matchingUsers = useMemo(() => {
        if (phoneDigits.length < 4) return [];
        return users.filter((u) =>
            normalizePhone(getUserPhone(u)).includes(phoneDigits)
        );
    }, [users, phoneDigits]);

    // The account the admin explicitly picked from the dropdown (if any).
    const selectedUser = useMemo(
        () => users.find((u) => u.id === form.userId),
        [users, form.userId]
    );

    const selectedCategory = useMemo(
        () => categories.find((c) => c.id === form.categoryId),
        [categories, form.categoryId]
    );
    const selectedSecondaryCategory = useMemo(
        () => categories.find((c) => c.id === form.secondaryCategoryId),
        [categories, form.secondaryCategoryId]
    );
    const selectedProvider = useMemo(
        () => providers.find((p) => p.id === form.serviceProviderId),
        [providers, form.serviceProviderId]
    );

    // Only active providers that work in the chosen service(s).
    const providersForCategory = useMemo(() => {
        if (!form.categoryId) return [];
        return providers.filter((p) =>
            providerCovers(p, form.categoryId, form.secondaryCategoryId)
        );
    }, [providers, form.categoryId, form.secondaryCategoryId]);

    // The booking as far as the commission split cares (see bookingSync).
    const bookingForSplit = useMemo(
        () => ({
            payableAmount: form.payableAmount,
            categoryId: form.categoryId,
            categoryName: selectedCategory?.name || "",
            secondaryCategoryId: form.secondaryCategoryId,
            secondaryCategoryName: selectedSecondaryCategory?.name || "",
            secondaryAmount: form.secondaryAmount === "" ? null : form.secondaryAmount,
        }),
        [
            form.payableAmount,
            form.categoryId,
            form.secondaryCategoryId,
            form.secondaryAmount,
            selectedCategory,
            selectedSecondaryCategory,
        ]
    );

    // What the provider will receive: each service's share minus that
    // service's commission — the override when set, else the provider's
    // default (frozen at assignment time by createBooking).
    const providerAmountPreview = useMemo(() => {
        if (!selectedProvider || form.payableAmount === "") return null;
        try {
            const overrides = normalizeCommissionOverrides(
                bookingForSplit,
                form.commissionOverrides
            );
            return computeBookingProviderAmount(bookingForSplit, selectedProvider, overrides);
        } catch {
            return null;
        }
    }, [selectedProvider, form.payableAmount, bookingForSplit, form.commissionOverrides]);

    // ---- field styling (matches providers.jsx) ---------------------------

    const getFieldClass = (field, extra = "") =>
        [
            "rounded border p-2 text-gray-900 focus:outline-none focus:ring-1",
            validationErrors[field]
                ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
            extra,
        ]
            .filter(Boolean)
            .join(" ");

    const getLabelClass = (field) =>
        `grid gap-1 text-sm font-medium ${validationErrors[field] ? "text-red-700" : "text-gray-700"
        }`;

    const renderFieldError = (field) =>
        validationErrors[field] ? (
            <span className="text-xs font-medium text-red-600">
                {validationErrors[field]}
            </span>
        ) : null;

    // ---- state updates ---------------------------------------------------

    const clearFieldError = (field) =>
        setValidationErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (value?.toString().trim()) clearFieldError(field);
        setSuccess("");
    };

    // Editing the phone clears any previously-picked account whose number no
    // longer matches, so a stale userId can't ride along on the booking.
    const handlePhoneChange = (value) => {
        // The +91 prefix is fixed in the UI, so the field holds bare digits.
        // Normalizing here also collapses a pasted +91… / spaced number.
        const digits = normalizePhone(value);
        setForm((prev) => {
            const picked = users.find((u) => u.id === prev.userId);
            const stillMatches =
                picked && normalizePhone(getUserPhone(picked)).includes(digits);
            return {
                ...prev,
                phoneNumber: digits,
                userId: stillMatches ? prev.userId : "",
            };
        });
        if (digits) clearFieldError("phoneNumber");
        setSuccess("");
    };

    // Admin explicitly picks an existing account → lock in its userId, and
    // snap the phone field to that account's number.
    const handleUserSelect = (userId) => {
        setForm((prev) => {
            if (!userId) return { ...prev, userId: "" };
            const picked = users.find((u) => u.id === userId);
            return {
                ...prev,
                userId,
                // Store bare digits behind the fixed +91 prefix.
                phoneNumber: picked
                    ? normalizePhone(getUserPhone(picked))
                    : prev.phoneNumber,
            };
        });
        setSuccess("");
    };

    // Switching either category resets the provider if it no longer matches.
    const handleCategoryChange = (field, categoryId) => {
        setForm((prev) => {
            const next = { ...prev, [field]: categoryId };
            const provider = providers.find((p) => p.id === prev.serviceProviderId);
            if (!providerCovers(provider, next.categoryId, next.secondaryCategoryId))
                next.serviceProviderId = "";
            return next;
        });
        clearFieldError(field);
        setSuccess("");
    };

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setValidationErrors({});
        setError("");
    };

    // ---- validation ------------------------------------------------------

    const validate = () => {
        const errors = {};
        // A picked account carries its own userId, so trust it even if its
        // stored number is oddly formatted. Otherwise require a valid phone.
        if (!selectedUser && normalizePhone(form.phoneNumber).length < 10)
            errors.phoneNumber = "Enter a valid 10-digit phone number.";

        if (!form.categoryId) errors.categoryId = "Select the primary service.";
        if (!form.serviceProviderId)
            errors.serviceProviderId = "Select a service provider.";
        if (!form.address.trim()) errors.address = "Address is required.";
        if (!form.pincode.trim()) errors.pincode = "Pincode is required.";
        // problemDescription is optional.

        if (form.payableAmount === "" || isNaN(Number(form.payableAmount)))
            errors.payableAmount = "Enter a valid payable amount.";
        else if (Number(form.payableAmount) < 0)
            errors.payableAmount = "Amount cannot be negative.";
        if (form.secondaryCategoryId) {
            const secondary = Number(form.secondaryAmount);
            if (form.secondaryAmount === "" || isNaN(secondary))
                errors.secondaryAmount = "Enter the secondary service's amount.";
            else if (secondary < 0)
                errors.secondaryAmount = "Amount cannot be negative.";
            else if (secondary > Number(form.payableAmount))
                errors.secondaryAmount = "Can't exceed the payable amount.";
        }
        try {
            normalizeCommissionOverrides(bookingForSplit, form.commissionOverrides);
        } catch (err) {
            errors.commissionOverrides = err.message;
        }
        if (form.usedCashpoints !== "" && isNaN(Number(form.usedCashpoints)))
            errors.usedCashpoints = "Enter a valid number.";

        return errors;
    };

    // ---- submit ----------------------------------------------------------

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccess("");

        const errors = validate();
        if (Object.keys(errors).length) {
            setValidationErrors(errors);
            setError("Please fix the highlighted fields.");
            return;
        }

        setLoading(true);
        setError("");
        setValidationErrors({});

        try {
            // Firestore assigns the id; we mirror the app and store it on the doc.
            const bookingRef = doc(collection(db, "bookings"));

            // Register the id BEFORE writing: the local snapshot "added" event
            // fires before setDoc resolves, so this must run first to suppress
            // the orders alarm for our own booking.
            onBeforeCreate?.(bookingRef.id);

            // Use the picked account's name; otherwise whatever the admin typed.
            // New (unpicked) customers get their user doc created by the
            // ensureBookingUser Cloud Function from userPhoneNumber on write.
            const resolvedName = selectedUser
                ? getUserName(selectedUser)
                : form.userName.trim();

            const bookingData = {
                id: bookingRef.id,
                userName: resolvedName,
                // Always store E.164 (+91…) to stay consistent with Firebase
                // Auth. For a picked user, re-derive it from their stored phone
                // (which may be bare digits); new customers get it from input.
                userPhoneNumber: selectedUser
                    ? toE164(getUserPhone(selectedUser)) ||
                    getUserPhone(selectedUser).toString()
                    : toE164(form.phoneNumber),

                // primary service (denormalized from the category)
                categoryId: form.categoryId,
                categoryName: selectedCategory?.name || "",
                imageOfCategory: selectedCategory?.image || "",
                colorOfCategory: selectedCategory?.color || "",

                // provider (denormalized from the provider profile)
                serviceProviderId: form.serviceProviderId,
                serviceProviderName: selectedProvider?.name || "",
                serviceProviderImage: selectedProvider?.image || "",
                providerPhoneNumber: (selectedProvider?.phone_number || "").toString(),

                address: form.address.trim(),
                pincode: form.pincode.trim(),
                problemDescription: form.problemDescription.trim(),

                status: form.status,
                dateTime: new Date(),
            };

            // Only set userId when the admin picked an existing account. Leaving
            // it out triggers ensureBookingUser to create/link the user by phone.
            if (selectedUser) bookingData.userId = selectedUser.id;

            // optional fields — only written when provided (matches toFirestore())
            if (form.payableAmount !== "")
                bookingData.payableAmount = Number(form.payableAmount);
            if (form.usedCashpoints !== "")
                bookingData.usedCashpoints = parseInt(form.usedCashpoints, 10);
            if (form.secondaryCategoryId) {
                bookingData.secondaryCategoryId = form.secondaryCategoryId;
                bookingData.secondaryCategoryName =
                    selectedSecondaryCategory?.name || "";
                bookingData.secondaryAmount = Number(form.secondaryAmount);
                bookingData.primaryAmount =
                    Number(form.payableAmount) - bookingData.secondaryAmount;
            }
            if (form.secondaryServiceOptionTitle.trim())
                bookingData.secondaryServiceOptionTitle =
                    form.secondaryServiceOptionTitle.trim();

            // Booking + provider copy are written in one batch.
            await createBooking(
                bookingData,
                selectedProvider,
                form.commissionOverrides
            );

            setSuccess(
                `Booking created for ${bookingData.userName || "new customer"} (${bookingData.userPhoneNumber
                }).`
            );
            resetForm();
            onCreated?.();
        } catch (err) {
            console.error("Error creating booking:", err);
            setError(
                `Failed to create booking: ${err?.message || "check the console for details."}`
            );
        } finally {
            setLoading(false);
        }
    };

    // ---- render ----------------------------------------------------------

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white border border-gray-200 rounded-md shadow-sm mb-8 overflow-hidden"
        >
            <div className="flex flex-col gap-3 border-b border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                        Create Booking
                    </h2>
                    <p className="text-sm text-gray-500">
                        Manually place a service booking on behalf of a customer.
                    </p>
                </div>
                {loadingData && (
                    <span className="text-sm text-gray-400">Loading options…</span>
                )}
            </div>

            {(error || success) && (
                <div className="px-4 pt-4">
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    {success && (
                        <p className="text-sm font-medium text-green-600">{success}</p>
                    )}
                </div>
            )}

            <div className="space-y-6 p-4">
                {/* ── Customer ─────────────────────────────────────────── */}
                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Customer
                    </h3>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <label className={getLabelClass("phoneNumber")}>
                            Phone number
                            <div
                                className={getFieldClass(
                                    "phoneNumber",
                                    "flex items-center gap-2 p-0 overflow-hidden"
                                )}
                            >
                                <span className="select-none border-r border-gray-300 bg-gray-50 px-3 py-2 text-gray-600">
                                    +91
                                </span>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={10}
                                    placeholder="10-digit number"
                                    className="min-w-0 flex-1 bg-transparent py-2 pr-2 text-gray-900 focus:outline-none"
                                    value={form.phoneNumber}
                                    onChange={(e) => handlePhoneChange(e.target.value)}
                                />
                            </div>
                            {renderFieldError("phoneNumber")}
                        </label>

                        {/* Existing accounts on this number — pick one to attach
                            its userId. Only shown when there are matches. */}
                        {matchingUsers.length > 0 && (
                            <label className="grid gap-1 text-sm font-medium text-gray-700">
                                Existing customer
                                <select
                                    className="rounded border border-gray-300 p-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    value={form.userId}
                                    onChange={(e) => handleUserSelect(e.target.value)}
                                >
                                    <option value="">
                                        {`Select account (${matchingUsers.length} match${matchingUsers.length === 1 ? "" : "es"})`}
                                    </option>
                                    {matchingUsers.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {getUserName(u)} ({getUserPhone(u)})
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>

                    {/* Name field for a brand-new customer (no account picked). */}
                    {!selectedUser && (
                        <label className="grid max-w-md gap-1 text-sm font-medium text-gray-700">
                            Customer name
                            <input
                                type="text"
                                placeholder="Optional — helps label new customers"
                                className="rounded border border-gray-300 p-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={form.userName}
                                onChange={(e) => setField("userName", e.target.value)}
                            />
                        </label>
                    )}

                    {/* Confirmation of the resolved customer. */}
                    {selectedUser ? (
                        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded border border-green-200 bg-green-50 p-3 text-sm">
                            <span className="font-medium text-green-700">
                                ✓ Existing customer
                            </span>
                            <span className="text-gray-700">
                                {getUserName(selectedUser)}
                            </span>
                            <span className="text-gray-500">
                                User ID: {selectedUser.id}
                            </span>
                        </div>
                    ) : (
                        phoneDigits.length >= 10 && (
                            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                                {matchingUsers.length > 0
                                    ? "Pick an existing account above to attach it, or leave unselected to create a new customer from this number."
                                    : "New customer — no account found for this number. The booking will be created and their account set up automatically from this phone number."}
                            </div>
                        )
                    )}
                </section>

                {/* ── Service & provider ───────────────────────────────── */}
                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Service &amp; Provider
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label className={getLabelClass("categoryId")}>
                            Primary service
                            <select
                                className={getFieldClass("categoryId")}
                                value={form.categoryId}
                                onChange={(e) => handleCategoryChange("categoryId", e.target.value)}
                            >
                                <option value="">Select service</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                            {renderFieldError("categoryId")}
                        </label>
                        <label className={getLabelClass("serviceProviderId")}>
                            Service provider
                            <select
                                className={getFieldClass("serviceProviderId")}
                                value={form.serviceProviderId}
                                onChange={(e) =>
                                    setField("serviceProviderId", e.target.value)
                                }
                            >
                                <option value="">Select provider</option>
                                {providersForCategory.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} ({p.phone_number || "no phone"})
                                    </option>
                                ))}
                            </select>
                            {renderFieldError("serviceProviderId")}
                            {form.categoryId && providersForCategory.length === 0 && (
                                <span className="text-xs text-amber-600">
                                    {form.secondaryCategoryId
                                        ? "No active provider works in both services."
                                        : "No active providers work in this service."}
                                </span>
                            )}
                        </label>
                    </div>

                    {selectedProvider && (
                        <div className="flex items-center gap-3 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
                            {selectedProvider.image && (
                                <img
                                    src={selectedProvider.image}
                                    alt={selectedProvider.name}
                                    className="h-12 w-12 rounded object-cover"
                                />
                            )}
                            <div>
                                <p className="font-medium text-gray-900">
                                    {selectedProvider.name}
                                </p>
                                <p className="text-gray-500">
                                    {selectedProvider.phone_number || "No phone"} ·{" "}
                                    {selectedProvider.location || "—"}
                                </p>
                                <p className="text-gray-700">
                                    Provider amount:{" "}
                                    {providerAmountPreview === null
                                        ? "—"
                                        : `₹${providerAmountPreview}`}
                                    {Object.keys(form.commissionOverrides).length > 0 &&
                                        " (custom cut)"}
                                </p>
                                {form.categoryId && (
                                    <div className="mt-2 space-y-1">
                                        <p className="text-xs text-gray-500">
                                            Override the commission for this booking only:
                                        </p>
                                        <CommissionOverrideFields
                                            services={bookingServices(bookingForSplit).map(
                                                (s) => ({
                                                    ...s,
                                                    defaultEntry:
                                                        selectedProvider.categories?.[
                                                            s.categoryId
                                                        ],
                                                })
                                            )}
                                            value={form.commissionOverrides}
                                            onChange={(v) =>
                                                setField("commissionOverrides", v)
                                            }
                                        />
                                        {renderFieldError("commissionOverrides")}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </section>

                {/* ── Job details ──────────────────────────────────────── */}
                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Job Details
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr]">
                        <label className={getLabelClass("address")}>
                            Address
                            <input
                                type="text"
                                placeholder="Full service address"
                                className={getFieldClass("address")}
                                value={form.address}
                                onChange={(e) => setField("address", e.target.value)}
                            />
                            {renderFieldError("address")}
                        </label>
                        <label className={getLabelClass("pincode")}>
                            Pincode
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="e.g. 110001"
                                className={getFieldClass("pincode")}
                                value={form.pincode}
                                onChange={(e) => setField("pincode", e.target.value)}
                            />
                            {renderFieldError("pincode")}
                        </label>
                    </div>
                    <label className={getLabelClass("problemDescription")}>
                        Problem / service description (optional)
                        <textarea
                            placeholder="What does the customer need?"
                            className={getFieldClass("problemDescription", "min-h-24")}
                            value={form.problemDescription}
                            onChange={(e) =>
                                setField("problemDescription", e.target.value)
                            }
                        />
                        {renderFieldError("problemDescription")}
                    </label>
                </section>

                {/* ── Payment & status ─────────────────────────────────── */}
                <section className="space-y-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Payment &amp; Status
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <label className={getLabelClass("payableAmount")}>
                            Payable amount (₹)
                            <input
                                type="number"
                                min="0"
                                step="1"
                                placeholder="e.g. 499"
                                className={getFieldClass("payableAmount")}
                                value={form.payableAmount}
                                onChange={(e) => setField("payableAmount", e.target.value)}
                            />
                            {renderFieldError("payableAmount")}
                        </label>
                        <label className={getLabelClass("usedCashpoints")}>
                            Cash points used
                            <input
                                type="number"
                                min="0"
                                step="1"
                                placeholder="Optional"
                                className={getFieldClass("usedCashpoints")}
                                value={form.usedCashpoints}
                                onChange={(e) => setField("usedCashpoints", e.target.value)}
                            />
                            {renderFieldError("usedCashpoints")}
                        </label>
                        <label className={getLabelClass("status")}>
                            Status
                            <select
                                className={getFieldClass("status")}
                                value={form.status}
                                onChange={(e) => setField("status", e.target.value)}
                            >
                                {STATUS_OPTIONS.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </section>

                {/* ── Secondary service (optional) ─────────────────────── */}
                <details className="rounded border border-gray-200 bg-gray-50 p-4">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700">
                        Secondary service (optional — service clubbing)
                    </summary>
                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <label className="grid gap-1 text-sm font-medium text-gray-700">
                            Secondary service
                            <select
                                className="rounded border border-gray-300 p-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={form.secondaryCategoryId}
                                onChange={(e) =>
                                    handleCategoryChange("secondaryCategoryId", e.target.value)
                                }
                            >
                                <option value="">None</option>
                                {categories
                                    .filter((c) => c.id !== form.categoryId)
                                    .map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                            </select>
                        </label>
                        <label className="grid gap-1 text-sm font-medium text-gray-700">
                            Secondary option title
                            <input
                                type="text"
                                placeholder="e.g. Deep clean add-on"
                                className="rounded border border-gray-300 p-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                value={form.secondaryServiceOptionTitle}
                                onChange={(e) =>
                                    setField("secondaryServiceOptionTitle", e.target.value)
                                }
                            />
                        </label>
                        {form.secondaryCategoryId && (
                            <label className={getLabelClass("secondaryAmount")}>
                                Secondary service amount (₹)
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="Part of the payable amount"
                                    className={getFieldClass("secondaryAmount")}
                                    value={form.secondaryAmount}
                                    onChange={(e) => setField("secondaryAmount", e.target.value)}
                                />
                                {renderFieldError("secondaryAmount")}
                                {form.secondaryAmount !== "" &&
                                    form.payableAmount !== "" && (
                                        <span className="text-xs text-gray-500">
                                            Primary service gets ₹
                                            {Number(form.payableAmount) -
                                                Number(form.secondaryAmount)}
                                        </span>
                                    )}
                            </label>
                        )}
                    </div>
                </details>
            </div>

            <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 p-4 sm:flex-row sm:items-center sm:justify-end">
                <button
                    type="button"
                    onClick={resetForm}
                    className="rounded border border-gray-300 px-5 py-2 font-medium text-gray-700 hover:bg-white"
                >
                    Clear
                </button>
                <button
                    type="submit"
                    disabled={loading || loadingData}
                    className="rounded bg-green-600 px-6 py-2 font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
                >
                    {loading ? "Creating…" : "Create Booking"}
                </button>
            </div>
        </form>
    );
};

export default CreateBookingForm;
